import { Component } from 'react';
import { GeneratorLayout, genStyles } from '../converter-layout/generator-layout';

type LibState =
    | { kind: 'idle' }
    | { kind: 'loading' }
    | { kind: 'ready'; argon2: any }
    | { kind: 'error'; message: string };

type Variant = 'argon2id' | 'argon2i' | 'argon2d';

interface State {
    password: string;
    salt: string;
    memorySize: number;
    iterations: number;
    parallelism: number;
    variant: Variant;
    hashLen: number;
    output: string;
    error: string | null;
    lib: LibState;
    busy: boolean;
    copied: boolean;
}

export default class Argon2GeneratorComponent extends Component<{ feature?: string }, State> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;
    state: State = {
        password: 'p4ssw0rd!',
        salt: '',
        memorySize: 19456,
        iterations: 2,
        parallelism: 1,
        variant: 'argon2id',
        hashLen: 32,
        output: '',
        error: null,
        lib: { kind: 'idle' },
        busy: false,
        copied: false,
    };

    componentDidMount() {
        const salt = new Uint8Array(16);
        crypto.getRandomValues(salt);
        this.setState({ salt: Array.from(salt).map(b => b.toString(16).padStart(2, '0')).join('') });
    }

    componentWillUnmount() {
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
    }

    private async ensureLib(): Promise<any | null> {
        if (this.state.lib.kind === 'ready') return this.state.lib.argon2;
        this.setState({ lib: { kind: 'loading' } });
        try {
            const argon2 = await import('hash-wasm');
            this.setState({ lib: { kind: 'ready', argon2 } });
            return argon2;
        } catch (e: any) {
            this.setState({ lib: { kind: 'error', message: e.message || 'Failed to load WASM' } });
            return null;
        }
    }

    generate = async () => {
        this.setState({ busy: true });
        const lib = await this.ensureLib();
        if (!lib) { this.setState({ busy: false }); return; }
        try {
            const saltBytes = this.state.salt.match(/.{1,2}/g)?.map(b => parseInt(b, 16)) || [];
            const saltArr = new Uint8Array(saltBytes);
            const fn = this.state.variant === 'argon2id' ? lib.argon2id
                    : this.state.variant === 'argon2i' ? lib.argon2i
                    : lib.argon2d;
            const hash = await fn({
                password: this.state.password,
                salt: saltArr,
                iterations: this.state.iterations,
                parallelism: this.state.parallelism,
                memorySize: this.state.memorySize,
                hashLength: this.state.hashLen,
                outputType: 'encoded',
            });
            this.setState({ output: hash, error: null, busy: false, copied: false });
        } catch (e: any) {
            this.setState({ error: e.message || 'Argon2 failed', output: '', busy: false });
        }
    };

    copyOutput = () => {
        if (!this.state.output) return;
        navigator.clipboard.writeText(this.state.output).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copied: true });
            this.copyTimeout = setTimeout(() => this.setState({ copied: false }), 2000);
        });
    };

    render() {
        const { password, salt, memorySize, iterations, parallelism, variant, hashLen, output, error, lib, busy, copied } = this.state;

        const form = (
            <>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Input</span></div>
                    <div className={genStyles.field}>
                        <label htmlFor="ar-password">Password</label>
                        <input id="ar-password" type="text" value={password} spellCheck={false} autoComplete="off"
                            onChange={e => this.setState({ password: e.target.value })} />
                    </div>
                    <div className={genStyles.field}>
                        <label htmlFor="ar-salt">Salt (hex)</label>
                        <input id="ar-salt" type="text" value={salt} spellCheck={false}
                            onChange={e => this.setState({ salt: e.target.value.replace(/[^0-9a-fA-F]/g, '') })} />
                    </div>
                    <div className={genStyles.field}>
                        <label htmlFor="ar-variant">Variant</label>
                        <select id="ar-variant" value={variant}
                            onChange={e => this.setState({ variant: e.target.value as Variant })}>
                            <option value="argon2id">argon2id (recommended)</option>
                            <option value="argon2i">argon2i</option>
                            <option value="argon2d">argon2d</option>
                        </select>
                    </div>
                </div>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Parameters</span></div>
                    <div className={genStyles.fieldRow}>
                        <div className={genStyles.field}>
                            <label htmlFor="ar-mem">Memory (KiB)</label>
                            <input id="ar-mem" type="number" inputMode="decimal" min={1024} max={1048576} step={1024} value={memorySize}
                                onChange={e => this.setState({ memorySize: +e.target.value || 19456 })} />
                            <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>OWASP min 19 MiB</span>
                        </div>
                        <div className={genStyles.field}>
                            <label htmlFor="ar-iter">Iterations</label>
                            <input id="ar-iter" type="number" inputMode="decimal" min={1} max={20} value={iterations}
                                onChange={e => this.setState({ iterations: +e.target.value || 2 })} />
                        </div>
                    </div>
                    <div className={genStyles.fieldRow}>
                        <div className={genStyles.field}>
                            <label htmlFor="ar-par">Parallelism</label>
                            <input id="ar-par" type="number" inputMode="decimal" min={1} max={8} value={parallelism}
                                onChange={e => this.setState({ parallelism: +e.target.value || 1 })} />
                        </div>
                        <div className={genStyles.field}>
                            <label htmlFor="ar-len">Hash length (bytes)</label>
                            <input id="ar-len" type="number" inputMode="decimal" min={16} max={64} value={hashLen}
                                onChange={e => this.setState({ hashLen: +e.target.value || 32 })} />
                        </div>
                    </div>
                    <button className={`${genStyles.iconBtn} ${genStyles.iconBtnPrimary}`} onClick={this.generate} disabled={busy || lib.kind === 'loading'} style={{ alignSelf: 'flex-start' }}>
                        {busy ? 'Hashing…' : lib.kind === 'loading' ? 'Loading WASM…' : '⚙ Hash'}
                    </button>
                </div>
            </>
        );

        const errMsg = error || (lib.kind === 'error' ? (lib as any).message : null);
        return (
            <GeneratorLayout
                form={form}
                previewLabel="Argon2 hash"
                previewMeta={output ? `${output.length} chars` : undefined}
                preview={<pre className={genStyles.previewArea} aria-label="Argon2 output">{output || (errMsg ? `Error: ${errMsg}` : 'Hash will appear here once you click Hash.')}</pre>}
                previewActions={
                    <button className={`${genStyles.iconBtn} ${copied ? genStyles.iconBtnSuccess : ''}`}
                        onClick={this.copyOutput} disabled={!output}>
                        {copied ? '✓ Copied' : '📋 Copy hash'}
                    </button>
                }
            />
        );
    }
}
